import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { trimStart } from 'lodash-es';

import { tokens } from '@hotjar/hotsauce';
import { Tooltip } from '@hotjar/insights-common/components/Tooltip';
import { ListUserCard } from './ListUserCard';
import { timeAgo } from '../lib/time';

import type { ContentToken } from '../domains/model';
import { MENTION_TRIGGER } from '../utils/content-utils';

const tooltipStyles = css`
  background: ${tokens.color.ui_background};
  padding: 0;
`;

const Wrapper = styled.div`
  overflow: hidden;
  ${tokens.text('caption')};
  color: ${tokens.color.text_default};
`;

const CommentAuthor = styled.span`
  font-weight: bold;
  margin-right: ${tokens.spacer.x0};
`;

const CommentText = styled.span`
  white-space: pre-wrap;
  word-break: break-word;
  user-select: text;
`;

const Mention = styled.span`
  color: ${tokens.BRAND_COLORS.cobalt_800};
`;

const Metadata = styled.div`
  font-size: 10px;
  color: ${tokens.color.text_disabled};
  ${tokens.truncateText()};
`;

const WEEK_IN_MS = 604800;
interface Props {
  value: Array<ContentToken>;
  created: number;
  authorName: string;
  className?: string;
}

export const Content = ({ authorName, value, created, className }: Props): JSX.Element => {
  const rendered = value.map((token) => {
    switch (token.type) {
      case 'mention': {
        const user = token.value;
        return (
          <Tooltip
            content={
              <ListUserCard
                name={user.name}
                email={user.email}
                avatar={user.avatar}
                focused={false}
              />
            }
            placement="bottom"
            arrow={false}
            inlinePositioning
            interactive
            css={tooltipStyles}
            key={token.value.id}
          >
            <Mention>@{trimStart(token.value.name, MENTION_TRIGGER)}</Mention>
          </Tooltip>
        );
      }
      default:
        return token.value;
    }
  });

  return (
    <Wrapper className={className}>
      <CommentAuthor className="comment-author">{authorName}</CommentAuthor>
      <CommentText className="comment-text">{rendered}</CommentText>
      <Metadata className="comment-metadata">{created && timeAgo(created, WEEK_IN_MS)}</Metadata>
    </Wrapper>
  );
};
